import { useState, useEffect, useCallback } from "react";
import type { JSX } from "react";
import { useModuleSettings } from "./hooks/useModuleSettings";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import type { EnumOption } from "./types";
import { Icon, PageSectionHeader, ConfirmDialog } from "../../components/ui";

interface ModelStatusEntry {
  readonly key: string;
  readonly downloaded: boolean;
  readonly download_size: string | null;
  readonly disk_usage: string | null;
}

export function SearchIndexingPanel(): JSX.Element {
  const runtime = useAppRuntime();
  const { entries, updateSetting } = useModuleSettings();

  // Extract search module settings
  const searchEntry = entries.find((e) => e.module_id === "search");
  const searchSchema = searchEntry?.schema;
  const embeddingModelField = searchSchema?.fields.find((f) => f.key === "embedding_model");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const embeddingModelOptions: readonly EnumOption[] =
    embeddingModelField?.field_type.type === "enum" ? embeddingModelField.field_type.options : [];
  const currentEmbeddingModel =
    searchEntry?.values.find((v) => v.key === "embedding_model")?.value ??
    embeddingModelField?.default_value ??
    "fastembed/multilingual-e5-small";

  // Model availability
  const [modelStatus, setModelStatus] = useState<readonly ModelStatusEntry[]>([]);

  const refreshModelStatus = useCallback(async () => {
    try {
      const resp = await runtime.transport.rpc<readonly ModelStatusEntry[]>(
        "search.model_status",
        {},
      );
      setModelStatus(resp);
    } catch {
      // ignore
    }
  }, [runtime.transport]);

  // Refresh model status on mount and every 5 seconds (to detect downloads)
  useEffect(() => {
    void refreshModelStatus();
    const interval = setInterval(() => void refreshModelStatus(), 5000);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { clearInterval(interval); };
  }, [refreshModelStatus]);

  // Indexing status polling
  const [indexStatus, setIndexStatus] = useState<{
    indexed: number;
    total: number;
    pending: number;
    percent: number;
    model: string;
    status: string;
  } | null>(null);

  const pollStatus = useCallback(async () => {
    try {
      const resp = await runtime.transport.rpc<{
        indexed: number;
        total: number;
        pending: number;
        percent: number;
        model: string;
        status: string;
      }>("search.indexing_status", {});
      setIndexStatus(resp);
    } catch {
      // ignore
    }
  }, [runtime.transport]);

  useEffect(() => {
    void pollStatus();
    const interval = setInterval(() => void pollStatus(), 3000);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { clearInterval(interval); };
  }, [pollStatus]);

  // ── Confirm dialog state ───────────────────────────────────────────────────
  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    detail?: string;
    confirmLabel: string;
    variant: "default" | "danger";
    onConfirm: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    variant: "default",
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onConfirm: () => {},
  });

  const closeDialog = useCallback(() => { setDialog((d) => ({ ...d, open: false })); }, []);

  // Switch model with smart dialog
  const handleSwitchModel = useCallback(
    (targetKey: string) => {
      const status = modelStatus.find((m) => m.key === targetKey);
      const opt = embeddingModelOptions.find((o) => o.value === targetKey);
      const label = opt?.label ?? targetKey;
      const isDownloaded = status?.downloaded ?? false;
      const isLocal = targetKey.startsWith("fastembed/");

      let message: string;
      let detail: string | undefined;

      if (isLocal && !isDownloaded) {
        const size = status?.download_size ?? "unknown size";
        message = `"${label}" needs to be downloaded first. This will use approximately ${size} of disk space.`;
        detail = "After downloading, all existing embeddings will be cleared and the entire database will be re-indexed with the new model. This may take a while.";
      } else {
        message = `All existing embeddings will be cleared and the entire database will be re-indexed with "${label}".`;
        detail = "This may take a while depending on the amount of data.";
      }

      setDialog({
        open: true,
        title: "Switch Embedding Model",
        message,
        detail,
        confirmLabel: isLocal && !isDownloaded ? "Download & Switch" : "Switch Model",
        variant: "default",
        onConfirm: () => {
          closeDialog();
          void updateSetting("search", "embedding_model", targetKey);
        },
      });
    },
    [modelStatus, embeddingModelOptions, updateSetting, closeDialog],
  );

  // Delete model with confirm dialog
  const handleDelete = useCallback(
    (key: string) => {
      const opt = embeddingModelOptions.find((o) => o.value === key);
      const status = modelStatus.find((m) => m.key === key);
      const label = opt?.label ?? key;
      const size = status?.disk_usage ?? "";

      setDialog({
        open: true,
        title: "Delete Model",
        message: `Delete "${label}"${size ? ` (${size})` : ""} from disk?`,
        detail: "You can re-download the model later if needed. This won't affect your indexed data.",
        confirmLabel: "Delete",
        variant: "danger",
        onConfirm: () => {
          closeDialog();
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          void (async () => {
            try {
              await runtime.transport.rpc("search.delete_model", { key });
              await refreshModelStatus();
            } catch {
              // ignore
            }
          })();
        },
      });
    },
    [embeddingModelOptions, modelStatus, runtime.transport, refreshModelStatus, closeDialog],
  );

  // Derived: downloaded models (excluding tfidf which is built-in)
  const downloadedModels = modelStatus.filter((m) => m.downloaded && m.key !== "tfidf");

  return (
    <div className="px-6 py-5 space-y-2">
      <PageSectionHeader
        title="Search & Indexing"
        description="Choose the embedding model for semantic search. Switching models will re-index all data."
      />

      <div className="h-3" />

      {/* Indexing status */}
      {indexStatus && (
        <div className="rounded-lg bg-surface-tertiary border border-edge px-4 py-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-content-secondary">
              {indexStatus.status === "indexing" ? (
                <span className="flex items-center gap-1.5">
                  <Icon name="loader" size={12} className="animate-spin text-accent" />
                  Indexing… {indexStatus.indexed.toLocaleString()} / {indexStatus.total.toLocaleString()}
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
                  {indexStatus.indexed.toLocaleString()} entities indexed
                </span>
              )}
            </span>
            <span className="text-content-muted">{indexStatus.model}</span>
          </div>
          {indexStatus.status === "indexing" && (
            <div className="mt-2 h-1 rounded-full bg-surface-tertiary overflow-hidden">
              <div
                className="h-full rounded-full bg-accent transition-all duration-500"
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                style={{ width: `${Math.min(indexStatus.percent, 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      <div className="h-3" />

      {/* Embedding model selector */}
      <span className="text-[11px] font-bold tracking-wider text-accent uppercase">
        Embedding Model
      </span>

      {embeddingModelOptions.length > 0 ? (
        <div className="rounded-xl bg-surface-tertiary border border-edge overflow-hidden mt-2">
          {embeddingModelOptions.map((opt) => {
            const active = opt.value === currentEmbeddingModel;
            const status = modelStatus.find((m) => m.key === opt.value);
            const isLocal = opt.value.startsWith("fastembed/");
            const needsApiKey = opt.value.startsWith("openai/");
            const needsOllama = opt.value.startsWith("ollama/");

            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  if (active) return;
                  handleSwitchModel(opt.value);
                }}
                className="flex items-center justify-between w-full px-4 py-3.5 border-b border-edge last:border-b-0 cursor-pointer text-left"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${active ? "text-content" : "text-content-secondary"}`}>
                      {opt.label}
                    </span>
                    {isLocal && status?.downloaded && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">
                        downloaded
                      </span>
                    )}
                    {isLocal && !status?.downloaded && status?.download_size && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400">
                        {status.download_size}
                      </span>
                    )}
                    {needsApiKey && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400">
                        API
                      </span>
                    )}
                    {needsOllama && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400">
                        Ollama
                      </span>
                    )}
                    {opt.value === "tfidf" && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">
                        built-in
                      </span>
                    )}
                  </div>
                  {opt.description && (
                    <span className="block text-[11px] text-content-muted mt-0.5">{opt.description}</span>
                  )}
                </div>
                {active ? (
                  <div className="w-5 h-5 rounded-full border-2 border-accent flex items-center justify-center shrink-0 ml-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-accent" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-content-muted shrink-0 ml-3" />
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl bg-surface-tertiary border border-edge px-4 py-8 text-center">
          <p className="text-sm text-content-tertiary">Loading models…</p>
        </div>
      )}

      {/* Downloaded models */}
      {downloadedModels.length > 0 && (
        <>
          <div className="h-3" />
          <span className="text-[11px] font-bold tracking-wider text-accent uppercase">
            Downloaded Models
          </span>
          <div className="rounded-xl bg-surface-tertiary border border-edge overflow-hidden mt-2">
            {downloadedModels.map((m) => {
              const isActive = m.key === currentEmbeddingModel;
              const opt = embeddingModelOptions.find((o) => o.value === m.key);
              const label = opt?.label ?? m.key;

              return (
                <div
                  key={m.key}
                  className="flex items-center justify-between px-4 py-3 border-b border-edge last:border-b-0"
                >
                  <div className="min-w-0">
                    <span className="text-sm text-content">{label}</span>
                    {m.disk_usage && (
                      <span className="text-xs text-content-muted ml-2">{m.disk_usage}</span>
                    )}
                    {isActive && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent/15 text-accent ml-2">
                        active
                      </span>
                    )}
                  </div>
                  {!isActive && (
                    <button
                      type="button"
                      onClick={() => { handleDelete(m.key); }}
                      className="text-xs px-2 py-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      title="Delete cached model"
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        detail={dialog.detail}
        confirmLabel={dialog.confirmLabel}
        variant={dialog.variant}
        onConfirm={dialog.onConfirm}
        onCancel={closeDialog}
      />
    </div>
  );
}
